import { useEffect, useRef, useState } from 'react'
import { useThree, type ThreeEvent } from '@react-three/fiber'
import {
  multiplicar,
  normalizar,
  paraArray,
  somar,
  type Vetor3,
} from '../../dominio/geometria/vetor3'
import type { FaixaGiro } from '../../dominio/geometria/anguloExtracao'
import { CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import { Vetor3D } from '../componentes/Vetor3D'
import { Rotulo3D } from '../componentes/Rotulo3D'

interface Props {
  centro: Vetor3
  direcao: Vetor3
  anguloGraus: number
  faixaGiro: FaixaGiro
  aberturaProximaNome?: string | null
  aoAlterarDirecao: (novaDirecao: Vetor3) => void
  comprimento?: number
  ativo?: boolean
}

const COR_POR_FAIXA: Record<FaixaGiro, string> = {
  axial: CORES_SEMANTICAS.cervicalAlinhado,
  leve: CORES_SEMANTICAS.cervicalAlinhado,
  moderado: CORES_SEMANTICAS.cervicalAtencao,
  acentuado: CORES_SEMANTICAS.cervicalExcessivo,
}

/**
 * Seta direcional interativa de extração 3D.
 *
 * Substitui o desenho manual de polilinha no solo:
 * Permite ao usuário arrastar a alça na ponta da seta em torno da vítima no
 * plano de apoio, exibindo o ângulo formado em tempo real e a abertura na mira.
 */
export function SetaDirecionalExtracao3D({
  centro,
  direcao,
  anguloGraus,
  faixaGiro,
  aberturaProximaNome,
  aoAlterarDirecao,
  comprimento: compSeta = 2.0,
  ativo = true,
}: Props) {
  const [arrastando, setArrastando] = useState(false)
  const controls = useThree((s) => s.controls) as { enabled?: boolean } | null
  const arrastandoRef = useRef(arrastando)
  arrastandoRef.current = arrastando

  const dirNorm = normalizar({ x: direcao.x, y: 0, z: direcao.z })
  const ponta = somar(centro, multiplicar(dirNorm, compSeta))
  const cor = COR_POR_FAIXA[faixaGiro] ?? '#10b981'

  useEffect(() => {
    if (controls) controls.enabled = !arrastando
    return () => {
      if (controls) controls.enabled = true
    }
  }, [arrastando, controls])

  useEffect(() => {
    if (!arrastando) return
    const soltar = () => setArrastando(false)
    window.addEventListener('pointerup', soltar)
    return () => window.removeEventListener('pointerup', soltar)
  }, [arrastando])

  const atualizarNoPonto = (pontoMundo: Vetor3) => {
    const deltaX = pontoMundo.x - centro.x
    const deltaZ = pontoMundo.z - centro.z
    if (Math.hypot(deltaX, deltaZ) > 0.15) {
      aoAlterarDirecao(normalizar({ x: deltaX, y: 0, z: deltaZ }))
    }
  }

  const textoRotulo = `${Math.round(anguloGraus)}°${
    aberturaProximaNome ? ` • ${aberturaProximaNome}` : ''
  }`
  const varianteRotulo =
    faixaGiro === 'axial' || faixaGiro === 'leve'
      ? 'trajetoria'
      : faixaGiro === 'moderado'
      ? 'zona'
      : 'risco'

  return (
    <group>
      <Vetor3D origem={centro} destino={ponta} cor={cor} espessura={6} />

      {/* Alça esférica de arraste na ponta da seta */}
      <mesh
        position={paraArray(ponta)}
        onPointerDown={(e: ThreeEvent<PointerEvent>) => {
          if (!ativo) return
          e.stopPropagation()
          setArrastando(true)
        }}
      >
        <sphereGeometry args={[arrastando ? 0.08 : 0.065, 18, 14]} />
        <meshStandardMaterial
          color={cor}
          emissive={cor}
          emissiveIntensity={arrastando ? 0.9 : 0.55}
          roughness={0.2}
        />
      </mesh>

      {/* Rótulo com o ângulo e a abertura correspondente */}
      <Rotulo3D
        posicao={[ponta.x, ponta.y + 0.16, ponta.z]}
        texto={textoRotulo}
        subtexto={arrastando ? 'arrastando direção' : undefined}
        variante={varianteRotulo}
        fator={5}
        sempreVisivel
      />

      {/* Plano invisível horizontal para capturar o arraste angular */}
      {ativo && (
        <mesh
          position={[centro.x, centro.y, centro.z]}
          rotation={[-Math.PI / 2, 0, 0]}
          visible={false}
          onPointerMove={(e: ThreeEvent<PointerEvent>) => {
            if (!arrastandoRef.current) return
            e.stopPropagation()
            const local = e.object.parent?.worldToLocal(e.point.clone()) ?? e.point
            atualizarNoPonto({ x: local.x, y: centro.y, z: local.z })
          }}
          onPointerUp={() => setArrastando(false)}
        >
          <planeGeometry args={[16, 16]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      )}
    </group>
  )
}
